import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Button, Row, Col, Badge, Table, Alert, Spinner, Modal } from 'react-bootstrap';
import { FiArrowLeft, FiCheckCircle, FiCalendar, FiMapPin, FiCloud, FiAlertTriangle, FiBarChart2, FiTrash2 } from 'react-icons/fi';
import { FaEuroSign } from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.css';
import { getActivityObservationById } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import { deleteActivityObservationRequest } from '../../store/actions/dataActions';
import PolicySumInsuredRows from '../../components/Common/PolicySumInsuredFields';

const Label = ({ children }) => (
    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{children}</div>
);

const Value = ({ children }) => (
    <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{children}</div>
);

const Section = ({ title, children }) => (
    <Card className="mb-3">
        <Card.Header style={{ fontWeight: 600 }}>{title}</Card.Header>
        <Card.Body>{children}</Card.Body>
    </Card>
);

const AdminActivityObservationDetail = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { observationId } = useParams();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [observation, setObservation] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const deleteObservationState = useSelector((state) => state.data.deleteActivityObservation);

    useEffect(() => {
        const fetchObservation = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await getActivityObservationById(observationId);
                // API returns { observation: { ... } }
                setObservation(data?.observation || null);
            } catch (e) {
                setError(handleApiError(e, 'Erreur lors du chargement de l\'observation'));
            } finally {
                setLoading(false);
            }
        };
        if (observationId) fetchObservation();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [observationId]);

    useEffect(() => {
        if (deleteObservationState.success) {
            setShowDeleteModal(false);
            navigate('/activity/observations');
        }
    }, [deleteObservationState.success, navigate]);

    const handleDelete = () => {
        if (observationId) {
            dispatch(deleteActivityObservationRequest(observationId));
        }
    };

    const handleConfirmDelete = () => {
        setShowDeleteModal(true);
    };

    if (loading) {
        return (
            <div className="dashboard-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail Observation</h2>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">Chargement de l'observation...</p>
                        </div>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail Observation</h2>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <Alert variant="danger">
                            <FiAlertTriangle className="me-2" />
                            {error}
                        </Alert>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (!observation) {
        return (
            <div className="dashboard-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail Observation</h2>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <p className="text-muted">Aucune donnée d'observation disponible pour l'identifiant: {observationId}.</p>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    const get = (obj, path, fallback = '—') => {
        try {
            const v = path.reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), obj);
            return v ?? fallback;
        } catch {
            return fallback;
        }
    };

    return (
        <div className="dashboard-page">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="page-title">
                    <FiBarChart2 className="me-2" />
                    Détail Observation
                </h2>
                <div className="d-flex gap-2">
                    <Button 
                        variant="outline-danger" 
                        onClick={handleConfirmDelete}
                        disabled={deleteObservationState.loading}
                    >
                        <FiTrash2 className="me-2" />
                        Supprimer
                    </Button>
                    <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                        <FiArrowLeft className="me-2" /> Retour
                    </Button>
                </div>
            </div>

            {deleteObservationState.error && (
                <Alert variant="danger" className="mb-3" dismissible onClose={() => {}}>
                    {deleteObservationState.error}
                </Alert>
            )}

            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirmer la suppression</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Êtes-vous sûr de vouloir supprimer cette observation ?</p>
                    <p className="text-muted small">
                        ID observation : <code>{observationId}</code>
                    </p>
                    <Alert variant="warning" className="mb-0">
                        <strong>Attention :</strong> Cette action est irréversible.
                    </Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleteObservationState.loading}>
                        Annuler
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={deleteObservationState.loading}>
                        {deleteObservationState.loading ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Suppression...
                            </>
                        ) : (
                            'Supprimer'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Dernière observation */}
            {observation?.last_observation_info && observation.last_observation_info.last_observation_id && (
                <Section title="Dernière observation">
                    <Row className="mb-2">
                        <Col md={4}><Label>ID Observation</Label><Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{get(observation, ['last_observation_info', 'last_observation_id'])}</Value></Col>
                        <Col md={4}><Label>Source de données</Label><Value>{get(observation, ['last_observation_info', 'last_data_source'])}</Value></Col>
                        <Col md={4}><Label>Total mm précipitation</Label><Value><FiCloud className="me-1" />{get(observation, ['last_observation_info', 'last_total_mm_prc'])}</Value></Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={4}><Label>Date init</Label><Value>
                            {get(observation, ['last_observation_info', 'last_observation_init_day'])}/
                            {get(observation, ['last_observation_info', 'last_observation_init_month'])}/
                            {get(observation, ['last_observation_info', 'last_observation_init_year'])}
                        </Value></Col>
                        <Col md={4}><Label>Heure exécution</Label><Value>{get(observation, ['last_observation_info', 'last_observation_run_hour'])}</Value></Col>
                        <Col md={4}><Label>Station ID</Label><Value>{get(observation, ['last_observation_info', 'last_station_id'])}</Value></Col>
                        <Col md={4}><Label>Nom station</Label><Value>{get(observation, ['last_observation_info', 'last_station_name'])}</Value></Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={4}><Label>Coordonnées station</Label><Value>
                            {get(observation, ['last_observation_info', 'last_latitude_station'])}, {get(observation, ['last_observation_info', 'last_longitude_station'])}
                        </Value></Col>
                        <Col md={4}><Label>Dernière modification</Label><Value>{get(observation, ['last_observation_info', 'last_observation_last_modified_at'])}</Value></Col>
                        <Col md={4}><Label>Modifié par</Label><Value>{get(observation, ['last_observation_info', 'last_observation_last_modified_by'])}</Value></Col>
                    </Row>
                    {observation.last_observation_info.last_observation_data && Array.isArray(observation.last_observation_info.last_observation_data) && observation.last_observation_info.last_observation_data.length > 0 && (
                        <div className="mt-3">
                            <Label>Données d'observation</Label>
                            <Table striped bordered hover size="sm" className="mt-2">
                                <thead>
                                    <tr>
                                        <th>Date/Heure</th>
                                        <th>Précipitation (mm)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {observation.last_observation_info.last_observation_data.map((data, idx) => (
                                        <tr key={idx}>
                                            <td>{data.time}</td>
                                            <td>{data.prcp}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Section>
            )}

            {/* Observations Meteostat */}
            {observation?.meteostat_observations_info && observation.meteostat_observations_info.meteostat_observation_id && (
                <Section title="Observations Meteostat">
                    <Row className="mb-2">
                        <Col md={4}><Label>ID Observation</Label><Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{get(observation, ['meteostat_observations_info', 'meteostat_observation_id'])}</Value></Col>
                        <Col md={4}><Label>Source de données</Label><Value>{get(observation, ['meteostat_observations_info', 'meteostat_data_source'])}</Value></Col>
                        <Col md={4}><Label>Total mm précipitation</Label><Value><FiCloud className="me-1" />{get(observation, ['meteostat_observations_info', 'meteostat_total_mm_prc'])}</Value></Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={4}><Label>Date init</Label><Value>
                            {get(observation, ['meteostat_observations_info', 'meteostat_observation_init_day'])}/
                            {get(observation, ['meteostat_observations_info', 'meteostat_observation_init_month'])}/
                            {get(observation, ['meteostat_observations_info', 'meteostat_observation_init_year'])}
                        </Value></Col>
                        <Col md={4}><Label>Heure exécution</Label><Value>{get(observation, ['meteostat_observations_info', 'meteostat_observation_run_hour'])}</Value></Col>
                        <Col md={4}><Label>Station ID</Label><Value>{get(observation, ['meteostat_observations_info', 'meteostat_station_id'])}</Value></Col>
                        <Col md={4}><Label>Nom station</Label><Value>{get(observation, ['meteostat_observations_info', 'meteostat_station_name'])}</Value></Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={4}><Label>Coordonnées station</Label><Value>
                            {get(observation, ['meteostat_observations_info', 'meteostat_latitude_station'])}, {get(observation, ['meteostat_observations_info', 'meteostat_longitude_station'])}
                        </Value></Col>
                        <Col md={4}><Label>Dernière modification</Label><Value>{get(observation, ['meteostat_observations_info', 'meteostat_observation_last_modified_at'])}</Value></Col>
                        <Col md={4}><Label>Modifié par</Label><Value>{get(observation, ['meteostat_observations_info', 'meteostat_observation_last_modified_by'])}</Value></Col>
                    </Row>
                    {observation.meteostat_observations_info.meteostat_observation_data && Array.isArray(observation.meteostat_observations_info.meteostat_observation_data) && observation.meteostat_observations_info.meteostat_observation_data.length > 0 && (
                        <div className="mt-3">
                            <Label>Données d'observation</Label>
                            <Table striped bordered hover size="sm" className="mt-2">
                                <thead>
                                    <tr>
                                        <th>Date/Heure</th>
                                        <th>Précipitation (mm)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {observation.meteostat_observations_info.meteostat_observation_data.map((data, idx) => (
                                        <tr key={idx}>
                                            <td>{data.time}</td>
                                            <td>{data.prcp}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Section>
            )}

            {/* Observations Meteomatics */}
            {observation?.meteomatics_observations_info && observation.meteomatics_observations_info.meteomatics_observation_id && (
                <Section title="Observations Meteomatics">
                    <Row className="mb-2">
                        <Col md={4}><Label>ID Observation</Label><Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{get(observation, ['meteomatics_observations_info', 'meteomatics_observation_id'])}</Value></Col>
                        <Col md={4}><Label>Source de données</Label><Value>{get(observation, ['meteomatics_observations_info', 'meteomatics_data_source'])}</Value></Col>
                        <Col md={4}><Label>Total mm précipitation</Label><Value><FiCloud className="me-1" />{get(observation, ['meteomatics_observations_info', 'meteomatics_total_mm_prc'])}</Value></Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={4}><Label>Date init</Label><Value>
                            {get(observation, ['meteomatics_observations_info', 'meteomatics_observation_init_day'])}/
                            {get(observation, ['meteomatics_observations_info', 'meteomatics_observation_init_month'])}/
                            {get(observation, ['meteomatics_observations_info', 'meteomatics_observation_init_year'])}
                        </Value></Col>
                        <Col md={4}><Label>Heure exécution</Label><Value>{get(observation, ['meteomatics_observations_info', 'meteomatics_observation_run_hour'])}</Value></Col>
                        <Col md={4}><Label>Station ID</Label><Value>{get(observation, ['meteomatics_observations_info', 'meteomatics_station_id'])}</Value></Col>
                        <Col md={4}><Label>Nom station</Label><Value>{get(observation, ['meteomatics_observations_info', 'meteomatics_station_name'])}</Value></Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={4}><Label>Coordonnées station</Label><Value>
                            {get(observation, ['meteomatics_observations_info', 'meteomatics_latitude_station'])}, {get(observation, ['meteomatics_observations_info', 'meteomatics_longitude_station'])}
                        </Value></Col>
                        <Col md={4}><Label>Dernière modification</Label><Value>{get(observation, ['meteomatics_observations_info', 'meteomatics_observation_last_modified_at'])}</Value></Col>
                        <Col md={4}><Label>Modifié par</Label><Value>{get(observation, ['meteomatics_observations_info', 'meteomatics_observation_last_modified_by'])}</Value></Col>
                    </Row>
                    {observation.meteomatics_observations_info.meteomatics_observation_data && Array.isArray(observation.meteomatics_observations_info.meteomatics_observation_data) && observation.meteomatics_observations_info.meteomatics_observation_data.length > 0 && (
                        <div className="mt-3">
                            <Label>Données d'observation</Label>
                            <Table striped bordered hover size="sm" className="mt-2">
                                <thead>
                                    <tr>
                                        <th>Date/Heure</th>
                                        <th>Précipitation (mm)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {observation.meteomatics_observations_info.meteomatics_observation_data.map((data, idx) => (
                                        <tr key={idx}>
                                            <td>{data.time}</td>
                                            <td>{data.prcp}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Section>
            )}

            {/* Observations Meteofrance */}
            {observation?.meteofrance_observations_info && observation.meteofrance_observations_info.meteofrance_observation_id && (
                <Section title="Observations Meteofrance">
                    <Row className="mb-2">
                        <Col md={4}><Label>ID Observation</Label><Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{get(observation, ['meteofrance_observations_info', 'meteofrance_observation_id'])}</Value></Col>
                        <Col md={4}><Label>Source de données</Label><Value>{get(observation, ['meteofrance_observations_info', 'meteofrance_data_source'])}</Value></Col>
                        <Col md={4}><Label>Total mm précipitation</Label><Value><FiCloud className="me-1" />{get(observation, ['meteofrance_observations_info', 'meteofrance_total_mm_prc'])}</Value></Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={4}><Label>Date init</Label><Value>
                            {get(observation, ['meteofrance_observations_info', 'meteofrance_observation_init_day'])}/
                            {get(observation, ['meteofrance_observations_info', 'meteofrance_observation_init_month'])}/
                            {get(observation, ['meteofrance_observations_info', 'meteofrance_observation_init_year'])}
                        </Value></Col>
                        <Col md={4}><Label>Heure exécution</Label><Value>{get(observation, ['meteofrance_observations_info', 'meteofrance_observation_run_hour'])}</Value></Col>
                        <Col md={4}><Label>Station ID</Label><Value>{get(observation, ['meteofrance_observations_info', 'meteofrance_station_id'])}</Value></Col>
                        <Col md={4}><Label>Nom station</Label><Value>{get(observation, ['meteofrance_observations_info', 'meteofrance_station_name'])}</Value></Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={4}><Label>Coordonnées station</Label><Value>
                            {get(observation, ['meteofrance_observations_info', 'meteofrance_latitude_station'])}, {get(observation, ['meteofrance_observations_info', 'meteofrance_longitude_station'])}
                        </Value></Col>
                        <Col md={4}><Label>Dernière modification</Label><Value>{get(observation, ['meteofrance_observations_info', 'meteofrance_observation_last_modified_at'])}</Value></Col>
                        <Col md={4}><Label>Modifié par</Label><Value>{get(observation, ['meteofrance_observations_info', 'meteofrance_observation_last_modified_by'])}</Value></Col>
                    </Row>
                    {observation.meteofrance_observations_info.meteofrance_observation_data && Array.isArray(observation.meteofrance_observations_info.meteofrance_observation_data) && observation.meteofrance_observations_info.meteofrance_observation_data.length > 0 && (
                        <div className="mt-3">
                            <Label>Données d'observation</Label>
                            <Table striped bordered hover size="sm" className="mt-2">
                                <thead>
                                    <tr>
                                        <th>Date/Heure</th>
                                        <th>Précipitation (mm)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {observation.meteofrance_observations_info.meteofrance_observation_data.map((data, idx) => (
                                        <tr key={idx}>
                                            <td>{data.time}</td>
                                            <td>{data.prcp}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Section>
            )}

            {/* Informations d'activité */}
            <Section title="Informations d'activité">
                <Row className="mb-2">
                    <Col md={4}><Label>Nom</Label><Value>{get(observation, ['activity_info', 'name'])}</Value></Col>
                    <Col md={4}><Label>Catégorie</Label><Value>{get(observation, ['activity_info', 'category'])}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Date début</Label><Value><FiCalendar className="me-1" />{get(observation, ['activity_info', 'activity_start_date'])}</Value></Col>
                    <Col md={4}><Label>Heure début</Label><Value>{get(observation, ['activity_info', 'activity_start_time'])}</Value></Col>
                    <Col md={4}><Label>Date fin</Label><Value><FiCalendar className="me-1" />{get(observation, ['activity_info', 'activity_end_date'])}</Value></Col>
                    <Col md={4}><Label>Heure fin</Label><Value>{get(observation, ['activity_info', 'activity_end_time'])}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Adresse</Label><Value><FiMapPin className="me-1" />{get(observation, ['activity_info', 'activity_address'])}</Value></Col>
                    <Col md={2}><Label>Code postal</Label><Value>{get(observation, ['activity_info', 'activity_zip_code'])}</Value></Col>
                    <Col md={4}><Label>Ville</Label><Value>{get(observation, ['activity_info', 'activity_city'])}</Value></Col>
                    <Col md={4}><Label>Pays</Label><Value>{get(observation, ['activity_info', 'activity_country'])}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Prix</Label><Value>{get(observation, ['activity_info', 'activity_price'])}</Value></Col>
                    <Col md={4}><Label>Durée</Label><Value>{get(observation, ['activity_info', 'duration'])}</Value></Col>
                    <Col md={4}><Label>Code activité</Label><Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{get(observation, ['activity_info', 'activity_code'])}</Value></Col>
                    <Col md={4}><Label>Numéro réservation</Label><Value>{get(observation, ['activity_info', 'reservation_number'])}</Value></Col>
                </Row>
                <Row>
                    <Col md={4}><Label>Latitude</Label><Value>{get(observation, ['activity_info', 'latitude'])}</Value></Col>
                    <Col md={4}><Label>Longitude</Label><Value>{get(observation, ['activity_info', 'longitude'])}</Value></Col>
                </Row>
            </Section>

            {/* Informations client */}
            <Section title="Informations client">
                <Row className="mb-2">
                    <Col md={4}><Label>Nom complet</Label><Value>{get(observation, ['client_info', 'first_name'])} {get(observation, ['client_info', 'last_name'])}</Value></Col>
                    <Col md={4}><Label>Email</Label><Value>{get(observation, ['client_info', 'email'])}</Value></Col>
                    <Col md={4}><Label>Téléphone</Label><Value>{get(observation, ['client_info', 'phone_number'])}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Code client</Label><Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{get(observation, ['client_info', 'client_code'])}</Value></Col>
                    <Col md={4}><Label>Statut</Label><Value>{get(observation, ['client_info', 'status'])}</Value></Col>
                    <Col md={4}><Label>Date d'inscription</Label><Value>{get(observation, ['client_info', 'date_joined'])}</Value></Col>
                </Row>
                <Row>
                    <Col md={4}><Label>Adresse</Label><Value>{get(observation, ['client_info', 'client_address'])}</Value></Col>
                    <Col md={2}><Label>Code postal</Label><Value>{get(observation, ['client_info', 'zip_code'])}</Value></Col>
                    <Col md={4}><Label>Ville</Label><Value>{get(observation, ['client_info', 'city'])}</Value></Col>
                    <Col md={4}><Label>Pays</Label><Value>{get(observation, ['client_info', 'country'])}</Value></Col>
                </Row>
            </Section>

            {/* Informations de police */}
            <Section title="Informations de police">
                <Row className="mb-2">
                    <Col md={4}><Label>Code police</Label><Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{get(observation, ['policy_info', 'policy_code'])}</Value></Col>
                    <Col md={4}><Label>Statut</Label><Value>{get(observation, ['policy_info', 'status'])}</Value></Col>
                    <Col md={4}><Label>Date UW</Label><Value>{get(observation, ['policy_info', 'uw_date'])}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Prix assurance (HT TSCA)</Label><Value>{get(observation, ['policy_info', 'insurance_price_excl_tsca'])}</Value></Col>
                    <Col md={4}><Label>Prix TTC (incl. TSCA)</Label><Value>{get(observation, ['policy_info', 'price_incl_tsca'])}</Value></Col>
                    <Col md={4}><Label>Montant TSCA</Label><Value>{get(observation, ['policy_info', 'tsca_amount'])}</Value></Col>
                    <Col md={4}><Label>TSCA (%)</Label><Value>{get(observation, ['policy_info', 'tsca'])}</Value></Col>
                </Row>
                <Row>
                    <Col md={4}><Label>Ratio Orakle</Label><Value>{get(observation, ['policy_info', 'orakle_price_ratio_excl_tsca'])}</Value></Col>
                    <Col md={4}><Label>Langue préférée</Label><Value>{get(observation, ['policy_info', 'preferred_language'])}</Value></Col>
                    <Col md={4}><Label>Date soumission RIB</Label><Value>{get(observation, ['policy_info', 'date_rib_submission'])}</Value></Col>
                </Row>
                <PolicySumInsuredRows source={observation?.policy_info} Label={Label} Value={Value} colMd={6} />
            </Section>

            {/* Informations de partenaire */}
            <Section title="Informations de partenaire">
                <Row className="mb-2">
                    <Col md={4}><Label>Nom</Label><Value>{get(observation, ['partner_info', 'partner_first_name'])} {get(observation, ['partner_info', 'partner_last_name'])}</Value></Col>
                    <Col md={4}><Label>Entreprise</Label><Value>{get(observation, ['partner_info', 'company_name'])}</Value></Col>
                    <Col md={4}><Label>Email</Label><Value>{get(observation, ['partner_info', 'email'])}</Value></Col>
                </Row>
                <Row>
                    <Col md={4}><Label>Code</Label><Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{get(observation, ['partner_info', 'code'])}</Value></Col>
                    <Col md={4}><Label>Téléphone</Label><Value>{get(observation, ['partner_info', 'phone_number'])}</Value></Col>
                    <Col md={4}><Label>Commission</Label><Value>{get(observation, ['partner_info', 'commission'])}</Value></Col>
                </Row>
            </Section>

            {/* Informations de référence */}
            <Section title="Informations de référence">
                <Row className="mb-2">
                    <Col md={4}><Label>Nom référence</Label><Value>{get(observation, ['reference_info', 'reference_name'])}</Value></Col>
                    <Col md={4}><Label>Catégorie</Label><Value>{get(observation, ['reference_info', 'reference_category'])}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Code référence</Label><Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{get(observation, ['reference_info', 'reference_code'])}</Value></Col>
                    <Col md={4}><Label>Déclenchement cumulé</Label><Value>{get(observation, ['reference_info', 'prcp_is_cumulated']) ? 'Oui' : 'Non'}</Value></Col>
                </Row>
                {observation?.reference_info?.SubCoverage && Array.isArray(observation.reference_info.SubCoverage) && observation.reference_info.SubCoverage.length > 0 && (
                    <div className="mt-3">
                        <Label>Sous-couvertures</Label>
                        <Table striped bordered hover size="sm" className="mt-2">
                            <thead>
                                <tr>
                                    <th>Type déclenchement</th>
                                    <th>Trigger Low</th>
                                    <th>Trigger Sup</th>
                                    <th>Unité</th>
                                    <th>Ville</th>
                                    <th>Pays</th>
                                </tr>
                            </thead>
                            <tbody>
                                {observation.reference_info.SubCoverage.map((sub, idx) => (
                                    <tr key={sub.sub_coverage_id || idx}>
                                        <td>{get(sub, ['trigger_type'])}</td>
                                        <td>{get(sub, ['trigger_low'])}</td>
                                        <td>{get(sub, ['trigger_sup'])}</td>
                                        <td>{get(sub, ['unit'])}</td>
                                        <td>{get(sub, ['city'])}</td>
                                        <td>{get(sub, ['country'])}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}
            </Section>

            {/* Vérification du sinistre */}
            <Section title="Vérification du sinistre">
                <Row className="mb-2">
                    <Col md={4}><Label>Observation déclenchée</Label><Value>
                        {(() => {
                            const isTriggeredObs = get(observation, ['CheckClaims', 'is_triggered_observation'], null);
                            if (isTriggeredObs === null) {
                                return <Badge bg="warning">En attente de traitement</Badge>;
                            }
                            return <Badge bg={isTriggeredObs ? 'success' : 'danger'}>{isTriggeredObs ? 'Oui' : 'Non'}</Badge>;
                        })()}
                    </Value></Col>
                    <Col md={4}><Label>Forecast déclenché</Label><Value>
                        {(() => {
                            const isTriggeredForecast = get(observation, ['CheckClaims', 'is_triggered_forecast'], null);
                            if (isTriggeredForecast === null) {
                                return <Badge bg="secondary">N/A</Badge>;
                            }
                            return <Badge bg={isTriggeredForecast ? 'success' : 'danger'}>{isTriggeredForecast ? 'Oui' : 'Non'}</Badge>;
                        })()}
                    </Value></Col>
                    <Col md={4}><Label>Forecast vérifié</Label><Value>{get(observation, ['CheckClaims', 'forecast_checked']) ? 'Oui' : 'Non'}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Trigger Low</Label><Value>{get(observation, ['CheckClaims', 'trigger_low'])}</Value></Col>
                    <Col md={4}><Label>Trigger Sup</Label><Value>{get(observation, ['CheckClaims', 'check_trigger_sup'])}</Value></Col>
                    <Col md={4}><Label>Déclenchement cumulé</Label><Value>{get(observation, ['CheckClaims', 'prcp_is_cumulated']) ? 'Oui' : 'Non'}</Value></Col>
                    <Col md={4}><Label>Observation vérifiée</Label><Value>{get(observation, ['CheckClaims', 'observation_checked']) ? 'Oui' : 'Non'}</Value></Col>
                </Row>
                <Row>
                    <Col md={4}><Label>Dernière modification</Label><Value>{get(observation, ['CheckClaims', 'check_claim_last_modified_at'])}</Value></Col>
                    <Col md={4}><Label>Modifié par</Label><Value>{get(observation, ['CheckClaims', 'check_claim_last_modified_by'])}</Value></Col>
                </Row>
            </Section>
        </div>
    );
};

export default AdminActivityObservationDetail;
